// Stripe's return page (/sucessfullOrder/<payment intent>): what the
// order's status says to show, and where its return URL sends the
// customer. Pure.

/** GET /app/checkPaymentIntentStatus/:id, as the API answers it (the
 * purchase's processing flags; `returnURl` is the API's spelling). */
export type PaymentIntentStatus = {
  isDataProcessed?: boolean;
  returnURl?: string;
  isError?: boolean;
  errorData?: { ux_message?: string } | null;
};

export type OrderState =
  /** Paid, the order is still being created: keep asking. */
  | { kind: "waiting" }
  /** Created: send the customer on. */
  | { kind: "done"; returnUrl: string }
  /** Created, but the API gave nowhere to go (v1 answered a 500). */
  | { kind: "done-nowhere" }
  /** Creating the order failed: the API's customer-facing words, if any. */
  | { kind: "failed"; message?: string };

/** The page's state from one status answer, in v1's order: processed
 * wins, then the error, else wait. */
export function orderState(status: PaymentIntentStatus): OrderState {
  if (status.isDataProcessed) {
    const url = typeof status.returnURl === "string" ? status.returnURl.trim() : "";
    return url ? { kind: "done", returnUrl: url } : { kind: "done-nowhere" };
  }
  if (status.isError) {
    const message = status.errorData && typeof status.errorData.ux_message === "string" ? status.errorData.ux_message.trim() : "";
    return { kind: "failed", message: message || undefined };
  }
  return { kind: "waiting" };
}

export type OrderReturn =
  /** A path on the school's site: served by the host the page is on. */
  | { kind: "site"; path: string }
  /** Somewhere else (the marketplace's receipt pages): as v1 went there. */
  | { kind: "away"; href: string };

/** Where an order's return URL sends the customer.
 *
 * The API builds it for v1: a path on the host the checkout started on
 * (`returnHost`), on the school's v1 widget host (`<slug>.<widget domain>`),
 * or on the marketplace. A path on this page's own host or on the school's
 * v1 host is kept on the current host (so a customer paying on the v2 site
 * stays on it, in its look, with the page's link query); anything else is
 * followed as v1 did. Only http(s) addresses are ever followed: anything
 * else answers null. */
export function orderReturnTarget(returnUrl: string, here: { origin: string; businessSlug?: string }): OrderReturn | null {
  const raw = returnUrl.trim();
  if (!raw) return null;
  // A site-relative path (never a protocol-relative "//host").
  if (raw.startsWith("/") && !raw.startsWith("//") && !raw.startsWith("/\\")) return { kind: "site", path: raw };
  let url: URL;
  let current: URL;
  try {
    url = new URL(raw);
    current = new URL(here.origin);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  const path = `${url.pathname}${url.search}${url.hash}`;
  if (url.host.toLowerCase() === current.host.toLowerCase()) return { kind: "site", path };
  const slug = here.businessSlug?.trim().toLowerCase();
  const labels = url.hostname.toLowerCase().split(".");
  if (slug && labels.length >= 2 && labels[0] === slug) return { kind: "site", path };
  return { kind: "away", href: url.toString() };
}
